import DocumentoPagoSelector, { DocumentoPago } from './DocumentoPagoSelector';
import { consultarDocumentoReserva } from '../../services/facturacionService';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Banknote, CreditCard, Smartphone, Wallet, SplitSquareHorizontal, Search } from 'lucide-react';
import { createPago, Pago } from '../../services/pagosService';
import { fetchTiposPagoPorPos, fetchTiposPago, TipoPago } from '../../services/tiposPagoService';
import { fetchCitas, Cita } from '../../services/citasService';
import { fetchSesionesPOS, SesionPOS } from '../../services/pos/sesionPOSService';
import { OperadorGrantRow, OperadorPinDialog, useOperadorGrant } from '../pos/OperadorPinDialog';

interface SesionPOSConSucursal extends SesionPOS {
    sucursal_id?: number;
}

interface AddPagosProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (newPago: Pago) => void;
}

const AddPagos: React.FC<AddPagosProps> = ({ isOpen, onClose, onCreated }) => {
    const [tiposPago, setTiposPago] = useState<TipoPago[]>([]);
    const [tipoPagoSeleccionado, setTipoPagoSeleccionado] = useState<TipoPago | null>(null);
    const [citas, setCitas] = useState<Cita[]>([]);
    const [filtroCodigo, setFiltroCodigo] = useState('');
    const [mostrarListaCitas, setMostrarListaCitas] = useState(false);
    const [citaSeleccionada, setCitaSeleccionada] = useState<Cita | null>(null);
    const [documentoPago, setDocumentoPago] = useState<DocumentoPago>('factura');
    const documentoEditado = useRef(false);
    useEffect(() => {
        let actual = true;
        documentoEditado.current = false;
        setDocumentoPago('factura');
        if (isOpen && citaSeleccionada) consultarDocumentoReserva(citaSeleccionada.id).then(doc => {
            if (actual && !documentoEditado.current) setDocumentoPago(doc.tipo);
        }).catch(() => {});
        return () => { actual = false; };
    }, [isOpen, citaSeleccionada?.id]);

    const inputCitaRef = useRef<HTMLInputElement>(null);
    const listaCitasRef = useRef<HTMLDivElement>(null);

    const [sesiones, setSesiones] = useState<SesionPOSConSucursal[]>([]);
    const [formData, setFormData] = useState({
        cita: '',
        tipo_pago: '',
        monto: '',
        efectivo_recibido: '',
        cambio: '',
        sesion: '', // campo unificado
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Cobrar desde una caja exige el grant del operador identificado por PIN
    // sobre ESA caja; cambiar de sesión lo descarta. Sin caja el pago es
    // administrativo (sin grant).
    const selectedSessionId = formData.sesion ? parseInt(formData.sesion) : null;
    const operador = useOperadorGrant(selectedSessionId);
    const requiereOperador = selectedSessionId !== null && !operador.token;

    const [montoAPagar, setMontoAPagar] = useState<number>(0);
    const [efectivoRecibidoNum, setEfectivoRecibidoNum] = useState<number>(0);
    const [cambioCalculado, setCambioCalculado] = useState<number>(0);

    const redondear = useCallback((valor: number) =>
        Math.round((valor + Number.EPSILON) * 100) / 100, []);

    const resetForm = useCallback(() => {
        setFormData({
            cita: '',
            tipo_pago: '',
            monto: '',
            efectivo_recibido: '',
            cambio: '',
            sesion: '',
        });
        setError('');
        setMontoAPagar(0);
        setEfectivoRecibidoNum(0);
        setCambioCalculado(0);
        setCitaSeleccionada(null);
        setTipoPagoSeleccionado(null);
        setFiltroCodigo('');
        setMostrarListaCitas(false);
    }, []);

    // Carga inicial al abrir el modal (sin seleccionar sesión automática)
    useEffect(() => {
        if (!isOpen) return;

        const cargarDatosIniciales = async () => {
            resetForm();

            // Sesiones activas
            const todasSesiones = await fetchSesionesPOS();
            const sesionesAbiertas = todasSesiones.filter(s => s.estado === 'abierta') as SesionPOSConSucursal[];
            setSesiones(sesionesAbiertas);

            // Citas con saldo pendiente
            const todasCitas = await fetchCitas();
            const citasConSaldo = todasCitas.filter(c => (c.total - c.monto_pagado) > 0);
            citasConSaldo.sort((a, b) => b.id - a.id);
            setCitas(citasConSaldo);

            // Tipos de pago por defecto (sin sesión)
            const todosTipos = await fetchTiposPago();
            setTiposPago(todosTipos);
            if (todosTipos.length > 0) {
                setFormData(prev => ({ ...prev, tipo_pago: todosTipos[0].id.toString() }));
                setTipoPagoSeleccionado(todosTipos[0]);
            }
        };

        cargarDatosIniciales();
    }, [isOpen, resetForm]);

    // Cargar tipos de pago según la sesión seleccionada
    useEffect(() => {
        if (!formData.sesion) return;

        const sesion = sesiones.find(s => s.id === parseInt(formData.sesion));
        if (!sesion) return;

        const cargarTiposPorSesion = async () => {
            const tipos = await fetchTiposPagoPorPos(sesion.punto_de_venta);
            setTiposPago(tipos);
            if (tipos.length > 0) {
                setFormData(prev => ({ ...prev, tipo_pago: tipos[0].id.toString() }));
                setTipoPagoSeleccionado(tipos[0]);
            } else {
                setFormData(prev => ({ ...prev, tipo_pago: '' }));
                setTipoPagoSeleccionado(null);
            }
        };
        cargarTiposPorSesion();
    }, [formData.sesion, sesiones]);

    const citasFiltradas = React.useMemo(() => {
        if (filtroCodigo.trim() === '') return citas;
        const filtro = filtroCodigo.toLowerCase();
        return citas.filter(cita => (cita.codigo || '').toLowerCase().includes(filtro));
    }, [citas, filtroCodigo]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'cita' && value) {
            const cita = citasFiltradas.find(c => c.id === parseInt(value));
            setCitaSeleccionada(cita || null);
            if (cita) {
                const saldo = redondear(cita.total - cita.monto_pagado);
                setMontoAPagar(saldo);
                setFormData(prev => ({ ...prev, monto: saldo.toFixed(2) }));
                setEfectivoRecibidoNum(saldo);
                setFormData(prev => ({ ...prev, efectivo_recibido: saldo.toFixed(2) }));
                setFiltroCodigo(cita.codigo || '');
            }
        }

        if (name === 'tipo_pago' && value) {
            const tipo = tiposPago.find(t => t.id === parseInt(value));
            setTipoPagoSeleccionado(tipo || null);
            if (tipo && citaSeleccionada) {
                const config = tipo.configuracion_detail;
                const saldo = redondear(citaSeleccionada.total - citaSeleccionada.monto_pagado);
                if (config && !config.permite_monto_personalizado) {
                    setMontoAPagar(saldo);
                    setFormData(prev => ({ ...prev, monto: saldo.toFixed(2) }));
                }
                if (config && !config.afecta_caja && !config.permite_monto_personalizado) {
                    setEfectivoRecibidoNum(saldo);
                    setFormData(prev => ({ ...prev, efectivo_recibido: saldo.toFixed(2) }));
                }
            }
        }

        if (name === 'monto' && value) {
            const monto = parseFloat(value) || 0;
            setMontoAPagar(monto);
            if (!tipoPagoSeleccionado?.configuracion_detail?.requiere_cambio) {
                setEfectivoRecibidoNum(monto);
                setFormData(prev => ({ ...prev, efectivo_recibido: monto.toFixed(2) }));
            }
        }

        if (name === 'efectivo_recibido' && value) {
            const efectivo = parseFloat(value) || 0;
            setEfectivoRecibidoNum(efectivo);
        }
    };

    const handleSeleccionarCita = (cita: Cita) => {
        setCitaSeleccionada(cita);
        setFormData(prev => ({ ...prev, cita: cita.id.toString() }));
        const saldo = redondear(cita.total - cita.monto_pagado);
        setMontoAPagar(saldo);
        setFormData(prev => ({ ...prev, monto: saldo.toFixed(2) }));
        setEfectivoRecibidoNum(saldo);
        setFormData(prev => ({ ...prev, efectivo_recibido: saldo.toFixed(2) }));
        setFiltroCodigo(cita.codigo || '');
        setMostrarListaCitas(false);
    };

    const handleFiltroCodigoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFiltroCodigo(e.target.value);
        setMostrarListaCitas(true);
        if (e.target.value === '') {
            setCitaSeleccionada(null);
            setFormData(prev => ({ ...prev, cita: '', monto: '' }));
            setMontoAPagar(0);
            setEfectivoRecibidoNum(0);
        }
    };

    useEffect(() => {
        if (documentoPago === 'factura' && citaSeleccionada) {
            setMontoAPagar(redondear(citaSeleccionada.total - citaSeleccionada.monto_pagado));
        }
    }, [documentoPago, citaSeleccionada, redondear]);

    // Calcular cambio
    useEffect(() => {
        const config = tipoPagoSeleccionado?.configuracion_detail;
        let nuevoCambio = 0;
        if (config) {
            if (config.requiere_cambio) {
                nuevoCambio = efectivoRecibidoNum > montoAPagar ? redondear(efectivoRecibidoNum - montoAPagar) : 0;
            } else if (config.permite_monto_personalizado && !config.afecta_caja && efectivoRecibidoNum > montoAPagar) {
                nuevoCambio = redondear(efectivoRecibidoNum - montoAPagar);
            }
        } else {
            nuevoCambio = efectivoRecibidoNum > montoAPagar ? redondear(efectivoRecibidoNum - montoAPagar) : 0;
        }
        setCambioCalculado(nuevoCambio);
        setFormData(prev => ({ ...prev, cambio: nuevoCambio.toFixed(2) }));
    }, [efectivoRecibidoNum, montoAPagar, tipoPagoSeleccionado, redondear]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');

        if (!formData.cita || !formData.tipo_pago || !formData.monto) {
            setError('Cita, tipo de pago y monto son obligatorios');
            setSubmitting(false);
            return;
        }

        const config = tipoPagoSeleccionado?.configuracion_detail;
        const montoFinal = redondear(montoAPagar);
        const efectivoFinal = redondear(efectivoRecibidoNum);
        const saldoMaximo = citaSeleccionada ? redondear(citaSeleccionada.total - citaSeleccionada.monto_pagado) : 0;

        if (config) {
            if (config.requiere_cambio && efectivoFinal < montoFinal) {
                setError('El efectivo recibido no puede ser menor al monto a pagar');
                setSubmitting(false);
                return;
            }
            if (config.permite_monto_personalizado && montoFinal > saldoMaximo) {
                setError(`El monto no puede superar el saldo pendiente de ${saldoMaximo.toFixed(2)} Bs.`);
                setSubmitting(false);
                return;
            }
        } else {
            if (montoFinal > saldoMaximo) {
                setError(`El monto no puede superar el saldo pendiente de ${saldoMaximo.toFixed(2)} Bs.`);
                setSubmitting(false);
                return;
            }
        }

        if (documentoPago === 'factura' && montoFinal !== saldoMaximo) {
            setError('Para factura debes cubrir el saldo completo de la reserva.');
            setSubmitting(false); return;
        }
        if (montoFinal <= 0) {
            setError('El monto debe ser mayor a 0');
            setSubmitting(false);
            return;
        }

        const payload: any = {
            documento_reserva: documentoPago,
            cita: parseInt(formData.cita),
            tipo_pago: parseInt(formData.tipo_pago),
            monto: montoFinal,
        };

        if (config) {
            if (config.requiere_cambio) {
                payload.efectivo_recibido = efectivoFinal;
                payload.cambio = cambioCalculado;
            } else if (config.permite_monto_personalizado && !config.afecta_caja && efectivoFinal > montoFinal) {
                payload.efectivo_recibido = efectivoFinal;
                payload.cambio = cambioCalculado;
            } else if (config.afecta_caja) {
                payload.efectivo_recibido = montoFinal;
            }
        } else {
            if (formData.efectivo_recibido) {
                payload.efectivo_recibido = parseFloat(formData.efectivo_recibido);
            }
            if (formData.cambio) {
                payload.cambio = parseFloat(formData.cambio);
            }
        }

        if (formData.sesion) {
            payload.sesion_pos = parseInt(formData.sesion);
            if (!operador.token) {
                setError('Identifica al operador de caja con su PIN antes de cobrar desde una sesión POS.');
                setSubmitting(false);
                return;
            }
        }

        try {
            const newPago = await createPago(payload, operador.token);
            onCreated(newPago);
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.error || err.response?.data?.detail || 'Error al crear el pago');
        } finally {
            setSubmitting(false);
        }
    };

    const getIconoTipoPago = (tipo: string) => {
        switch (tipo) {
            case 'efectivo': return <Banknote className="w-4 h-4" />;
            case 'banco': return <CreditCard className="w-4 h-4" />;
            case 'otro': return <Smartphone className="w-4 h-4" />;
            default: return <Wallet className="w-4 h-4" />;
        }
    };

    const getColorConfiguracion = (afectaCaja: boolean, requiereCambio: boolean) => {
        if (!afectaCaja) return { bg: 'bg-purple-50/50', text: 'text-purple-700', border: 'border-purple-200', iconBg: 'bg-purple-100' };
        if (afectaCaja && requiereCambio) return { bg: 'bg-[#61257b]/10', text: 'text-[#61257b]', border: 'border-[#61257b]/20', iconBg: 'bg-[#61257b]/10' };
        return { bg: 'bg-[#8b3fa8]/10', text: 'text-[#00a386]', border: 'border-[#8b3fa8]/20', iconBg: 'bg-[#8b3fa8]/10' };
    };

    // Cerrar lista de citas al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (listaCitasRef.current && !listaCitasRef.current.contains(event.target as Node) &&
                inputCitaRef.current && !inputCitaRef.current.contains(event.target as Node)) {
                setMostrarListaCitas(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Estilos scroll
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .custom-scroll::-webkit-scrollbar { width: 6px; }
            .custom-scroll::-webkit-scrollbar-track { background: transparent; }
            .custom-scroll::-webkit-scrollbar-thumb { background-color: rgba(97, 37, 123, 0.3); border-radius: 20px; }
            .custom-scroll::-webkit-scrollbar-thumb:hover { background-color: rgba(97, 37, 123, 0.5); }
        `;
        document.head.appendChild(style);
        return () => { document.head.removeChild(style); };
    }, []);

    if (!isOpen) return null;

    const config = tipoPagoSeleccionado?.configuracion_detail;
    const permitePersonalizado = documentoPago === 'prefactura' && (config?.permite_monto_personalizado ?? false);
    const afectaCaja = config?.afecta_caja ?? true;
    const requiereCambio = config?.requiere_cambio ?? false;
    const esPagoDigitalConCambio = permitePersonalizado && !afectaCaja && efectivoRecibidoNum > montoAPagar;
    const mostrarEfectivoRecibido = config
        ? (afectaCaja || esPagoDigitalConCambio || (permitePersonalizado && !afectaCaja))
        : true;
    const mostrarCambio = config
        ? (requiereCambio || esPagoDigitalConCambio)
        : (efectivoRecibidoNum > montoAPagar);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
            <div className="bg-white p-6 rounded-xl w-full max-w-2xl mx-4 border border-gray-100 shadow-xl relative max-h-[90vh] overflow-y-auto custom-scroll">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Registrar Nuevo Pago</h3>
                        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <DocumentoPagoSelector value={documentoPago}
                        onChange={tipo => { documentoEditado.current = true; setDocumentoPago(tipo); }}
                        disabled={submitting} destino="la reserva" />
                    {/* Sesión POS */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Sesión POS (opcional)</label>
                        <select
                            name="sesion"
                            value={formData.sesion}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Seleccionar sesión (opcional)</option>
                            {sesiones.map(sesion => (
                                <option key={sesion.id} value={sesion.id}>
                                    {sesion.codigo || `Sesión #${sesion.id}`} - {sesion.punto_de_venta_nombre}
                                    {' - Saldo: '}{sesion.saldo_actual.toFixed(2)} Bs.
                                </option>
                            ))}
                        </select>
                        {sesiones.length === 0 && (
                            <p className="text-xs text-amber-600">No hay sesiones POS activas. El pago se registrará sin asociar a una sesión.</p>
                        )}
                        {selectedSessionId !== null && (
                            <OperadorGrantRow grant={operador.grant} disabled={submitting} onIdentify={operador.openDialog} />
                        )}
                    </div>

                    {/* Búsqueda de Cita */}
                    <div className="relative space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Cita *</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                ref={inputCitaRef}
                                type="text"
                                value={filtroCodigo}
                                onChange={handleFiltroCodigoChange}
                                onFocus={() => setMostrarListaCitas(true)}
                                placeholder="Buscar por código de cita..."
                                className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoComplete="off"
                            />
                            {filtroCodigo && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFiltroCodigo('');
                                        setCitaSeleccionada(null);
                                        setFormData(prev => ({ ...prev, cita: '', monto: '' }));
                                        setMontoAPagar(0);
                                        setEfectivoRecibidoNum(0);
                                        inputCitaRef.current?.focus();
                                    }}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {mostrarListaCitas && (
                            <div
                                ref={listaCitasRef}
                                className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto custom-scroll"
                            >
                                {citasFiltradas.length === 0 ? (
                                    <div className="px-4 py-3 text-sm text-gray-500">
                                        {filtroCodigo ? 'No hay citas que coincidan' : 'No hay citas pendientes'}
                                    </div>
                                ) : (
                                    citasFiltradas.map(cita => {
                                        const saldo = (cita.total - cita.monto_pagado).toFixed(2);
                                        const cliente = cita.cliente_info
                                            ? `${cita.cliente_info.first_name} ${cita.cliente_info.last_name}`
                                            : `Cliente #${cita.cliente}`;
                                        const estaSeleccionada = citaSeleccionada?.id === cita.id;
                                        return (
                                            <div
                                                key={cita.id}
                                                onClick={() => handleSeleccionarCita(cita)}
                                                className={`px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-0 ${estaSeleccionada ? 'bg-blue-50' : ''}`}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium text-gray-900">{cita.codigo || 'Sin código'}</span>
                                                    <span className="text-sm text-gray-600">Pendiente: {saldo} Bs.</span>
                                                </div>
                                                <div className="text-sm text-gray-600 truncate">{cliente}</div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}

                        <input type="hidden" name="cita" value={formData.cita} />

                        {citaSeleccionada && (
                            <p className="text-xs text-green-600">
                                Cita seleccionada: {citaSeleccionada.codigo || 'Sin código'} - {citaSeleccionada.cliente_info?.first_name} {citaSeleccionada.cliente_info?.last_name}
                            </p>
                        )}
                    </div>

                    {/* Tipo de Pago */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Tipo de Pago *</label>
                        <select
                            name="tipo_pago"
                            value={formData.tipo_pago}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        >
                            <option value="">Seleccionar</option>
                            {tiposPago.map(tipo => (
                                <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
                            ))}
                        </select>
                        {config && (
                            <div className={`mt-3 p-4 border rounded-lg ${getColorConfiguracion(afectaCaja, requiereCambio).bg} ${getColorConfiguracion(afectaCaja, requiereCambio).border}`}>
                                <div className="flex items-start">
                                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center mr-3 ${getColorConfiguracion(afectaCaja, requiereCambio).iconBg}`}>
                                        <div className={getColorConfiguracion(afectaCaja, requiereCambio).text}>
                                            {getIconoTipoPago(config.tipo)}
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <p className={`font-medium ${getColorConfiguracion(afectaCaja, requiereCambio).text}`}>
                                            {config.nombre}
                                        </p>
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${afectaCaja ? 'bg-[#8b3fa8]/20 text-[#00a386]' : 'bg-purple-100 text-purple-700'}`}>
                                                {afectaCaja ? 'Afecta a caja' : 'No afecta a caja'}
                                            </span>
                                            {requiereCambio && (
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#61257b]/20 text-[#61257b]">
                                                    Requiere cambio
                                                </span>
                                            )}
                                            {permitePersonalizado && (
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                                    <SplitSquareHorizontal className="w-3 h-3 mr-1" />
                                                    Pago parcial permitido
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Monto a Pagar */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Monto a Pagar *
                            {permitePersonalizado && (
                                <span className="ml-2 text-xs font-normal text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                                    Monto libre
                                </span>
                            )}
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            name="monto"
                            value={formData.monto}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${permitePersonalizado ? 'border-purple-300 bg-white' : 'border-gray-300 bg-gray-50'}`}
                            required
                            readOnly={!permitePersonalizado && !!config}
                            min={0.01}
                            max={citaSeleccionada ? redondear(citaSeleccionada.total - citaSeleccionada.monto_pagado) : undefined}
                        />
                        {permitePersonalizado && citaSeleccionada && (
                            <p className="text-xs text-purple-600">
                                Máx: {redondear(citaSeleccionada.total - citaSeleccionada.monto_pagado).toFixed(2)} Bs.
                            </p>
                        )}
                    </div>

                    {/* Efectivo Recibido */}
                    {mostrarEfectivoRecibido && (
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                {config?.requiere_cambio || esPagoDigitalConCambio ? 'Monto Recibido *' : 'Monto a Pagar'}
                                {!afectaCaja && permitePersonalizado && (
                                    <span className="ml-2 text-xs font-normal text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                                        QR / Digital
                                    </span>
                                )}
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                name="efectivo_recibido"
                                value={formData.efectivo_recibido}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                min={0}
                                readOnly={config ? (!config.requiere_cambio && !(permitePersonalizado && !afectaCaja)) : false}
                            />
                            <p className="text-xs text-gray-500">
                                {config?.requiere_cambio
                                    ? 'Ingrese el monto recibido para calcular el cambio'
                                    : permitePersonalizado && !afectaCaja
                                        ? 'Si recibe más del monto, el cambio se registrará como salida de caja en efectivo'
                                        : 'Pago exacto - no requiere cambio'}
                            </p>
                        </div>
                    )}

                    {/* Cambio a Entregar */}
                    {mostrarCambio && (
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Cambio a Entregar
                                {esPagoDigitalConCambio && !afectaCaja && (
                                    <span className="ml-2 text-xs font-normal text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                                        Salida de caja en efectivo
                                    </span>
                                )}
                            </label>
                            <div className={`w-full px-3 py-2 border rounded-lg font-semibold ${cambioCalculado > 0 ? 'border-[#8b3fa8]/30 bg-[#8b3fa8]/10 text-[#00a386]' : 'border-gray-300 bg-gray-50 text-gray-900'}`}>
                                {cambioCalculado.toFixed(2)} <span className="text-sm font-normal">Bs.</span>
                            </div>
                            {esPagoDigitalConCambio && cambioCalculado > 0 && (
                                <p className="text-xs text-orange-600">
                                    Este cambio se registrará como salida de caja física de {cambioCalculado.toFixed(2)} Bs.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Advertencia pago digital sin cambio */}
                    {!afectaCaja && tipoPagoSeleccionado && !esPagoDigitalConCambio && (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-start">
                            <Smartphone className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-purple-800 text-sm">Pago Digital</p>
                                <p className="text-xs text-purple-600">
                                    {permitePersonalizado
                                        ? 'Si el cliente envía más del monto indicado, ingrese el monto recibido para calcular el cambio a devolver.'
                                        : 'Este pago no afectará a la caja física.'}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Botones */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || requiereOperador}
                            title={requiereOperador ? 'Identifica al operador de caja con su PIN para cobrar desde la sesión' : undefined}
                            className="px-4 py-2 text-sm bg-[#c1348b] hover:bg-[#8f1f64] text-white rounded-lg transition-colors font-medium flex items-center justify-center min-w-[120px] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Guardando...
                                </span>
                            ) : (
                                'Crear Pago'
                            )}
                        </button>
                    </div>
                </form>
            </div>
            <OperadorPinDialog open={operador.dialogOpen} sesionId={selectedSessionId} onClose={operador.closeDialog} onGrant={operador.install} />
        </div>
    );
};

export default AddPagos;
